import { Divider, Button, FormControl, InputAdornment, ListItemText, MenuItem, Paper, Select, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, Tooltip, CircleMarker, Polygon, SVGOverlay } from 'react-leaflet';
import { scaleSequential } from 'd3-scale';
import { interpolateTurbo } from 'd3-scale-chromatic';
import * as turf from '@turf/turf';
import { useSnackbar } from 'notistack';

import { useSelector } from 'react-redux';
import { Add, AdsClick, Cancel, Check, ContentCopy, Delete, FastForward, FastRewind, PlayArrow, RestartAlt, Save, SkipNext, SkipPrevious, Stop } from '@mui/icons-material';
import { useTheme } from '@mui/styles';

// import { ReactGrid } from "@silevis/reactgrid";
// import "@silevis/reactgrid/styles.css";
import ReactGrid from "../ReactGridComponent";

import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { chartColors } from '../../helpers/chartColors';
import { WellLabel, WellboreCrossSection, calculateCenterPoint, calculateLineCoordinates, findTVD, getMiddlePoint, interpolateCoordinates, rotatePolyline, surveyToCoordinates } from './RTFOMapWithWellbore';
import RTFOMapLegend from './RTFOMapLegend';
import { RTFOWellboreTrajectoryChart } from './RTFOWellboreTrajectoryChart';
import { RTFOFracSimulatorDataTable } from './RTFOFracSimulatorDataTable';
import { useSocket } from '../../context/useSocket';
import ScrollZoomControl from './ScrollZoomControl';
import ScrollZoomControlOverlay from './ScrollZoomControlOverlay';
import { rtfoConversionFactors } from './rtfoConversionFactors';


const RTFOFracSimulator = () => {

  const theme = useTheme();

  const mapRef = useRef(null);
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();


  const darkMode = user?.value?.darkMode;
  const units = currentRTFO?.settings?.units || 'metric';

  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);

      if (!response) return;

      if (response.messageType === 'fracSimCasesUpdated') {
        const success = response?.payload?.success;

        if (success) {
          enqueueSnackbar(`Simulator settings saved successfully`, {
            variant: 'success',
            persist: false
          });
        } else {
          enqueueSnackbar(`Simulator settings could not be saved`, {
            variant: 'error',
            persist: false
          });
        };
      };
    };
  };

  const [isAddNewFracSimCaseVisible, setIsAddNewFracSimCaseVisible] = useState(false);
  const [isPlayblast, setIsPlayblast] = useState(false);
  const [fracLengthDisplay, setFracLengthDisplay] = useState(0.5);
  const [isCaseEdited, setIsCaseEdited] = useState(false);
  const [interactiveMode, setInteractiveMode] = useState(false);
  const [scrollZoomControlOverlayVisible, setScrollZoomControlOverlayVisible] = useState(false);


  const wellNames = Object.keys(currentRTFO?.perfSchedule?.wells)?.sort();

  const actualSequence = useMemo(() => {
    try {

      const allStageNumbers = wellNames?.map(w => Object.keys(currentRTFO?.perfSchedule?.wells?.[w]?.stages)?.map(s => Number(s)))?.flat(Infinity);
      const maxStageNumber = Math.max(...allStageNumbers);
      const maxRowNumber = maxStageNumber - 1;

      let completedStages = [...currentRTFO?.stages];
      completedStages.sort((a, b) => currentRTFO?.stageData?.[a]?.stageStartTimestamp - currentRTFO?.stageData?.[b]?.stageStartTimestamp);

      const rowsObj = {};

      for (let idx = 0; idx < completedStages?.length; idx++) {
        const stageName = completedStages[idx];
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        const stageNumber = stageName?.split('_')?.[2];

        const stageRow = Number(String(stageNumber)?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()) - 1;
        const stageNumberInSequence = idx + 1;

        if (!rowsObj[stageRow]) {
          const row = {}
          for (const w of wellNames) {
            row[w] = w === wellName ? stageNumberInSequence : null;
          };
          rowsObj[stageRow] = row;
        } else {
          rowsObj[stageRow][wellName] = stageNumberInSequence
        };
      };

      // fill the gaps
      for (let rowNumber = 0; rowNumber <= Object.keys(rowsObj)?.slice(-1)?.[0]; rowNumber++) {
        if (!rowsObj[rowNumber]) {
          const row = {}
          for (const w of wellNames) {
            row[w] = null;
          };
          rowsObj[rowNumber] = row;
        };
      };

      let rows = [...Object.values(rowsObj)];

      const rowsCompleted = rows?.length;

      for (let rowNumber = rowsCompleted; rowNumber < maxRowNumber; rowNumber++) {
        const row = {}
        for (const w of wellNames) {
          row[w] = null;
        };
        rows.push(row);
      };

      return rows;
    } catch (error) {
      console.log(error)
    };
  }, [currentRTFO?.perfSchedule?.wells, currentRTFO?.stageData, currentRTFO?.stages, wellNames])

  const defaultSequence = useMemo(() => {
    try {
      const allStageNumbers = Object.keys(currentRTFO?.perfSchedule?.wells)?.map(w => Object.keys(currentRTFO?.perfSchedule?.wells?.[w]?.stages)?.map(s => Number(s)))?.flat(Infinity);
      const maxStageNumber = Math.max(...allStageNumbers);

      const rows = [];

      let currentNumber = 1;

      for (let rowNumber = 0; rowNumber < maxStageNumber; rowNumber++) {

        const row = {};

        for (const wellName of wellNames) {
          if (currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[String(rowNumber + 1)]) {
            row[wellName] = currentNumber;
            currentNumber++;
          } else {
            row[wellName] = null;
          };
        };

        rows.push(row);
      };

      return rows;
    } catch (error) {
      console.log(error)
      return [];
    };
  }, [currentRTFO?.perfSchedule?.wells, wellNames]);

  const blankSequence = useMemo(() => {
    try {
      const allStageNumbers = Object.keys(currentRTFO?.perfSchedule?.wells)?.map(w => Object.keys(currentRTFO?.perfSchedule?.wells?.[w]?.stages)?.map(s => Number(s)))?.flat(Infinity);
      const maxStageNumber = Math.max(...allStageNumbers);

      const rows = [];

      for (let rowNumber = 0; rowNumber < maxStageNumber; rowNumber++) {

        const row = {};

        for (const wellName of wellNames) {
          row[wellName] = null;
        };

        rows.push(row);
      };

      return rows;
    } catch (error) {
      console.log(error)
      return []
    }
  }, [currentRTFO?.perfSchedule?.wells, wellNames])

  const defaultModelParameters = useMemo(() => {
    return {
      fracHalfLength: 300,
      youngModulus: currentRTFO?.settings?.youngModulus || 3046000,
      fracWidth: currentRTFO?.settings?.fracWidth || 0.25,
      t: currentRTFO?.settings?.t || 1.2
    };
  }, [currentRTFO?.settings?.fracWidth, currentRTFO?.settings?.t, currentRTFO?.settings?.youngModulus])

  const isEmptySequence = !currentRTFO?.stages?.length > 0;

  const [fracSimCases, setFracSimCases] = useState(() => {

    const fracSimCases = {
      [!isEmptySequence ? 'Actual Sequence' : 'Sequential']: {
        name: !isEmptySequence ? 'Actual Sequence' : 'Sequential',
        source: !isEmptySequence ? 'Actual Sequence' : 'Sequential',
        fracHalfLength: defaultModelParameters.fracHalfLength,
        youngModulus: defaultModelParameters.youngModulus,
        fracWidth: defaultModelParameters.fracWidth,
        t: defaultModelParameters.t,
        pumpingSequence: !isEmptySequence ? actualSequence : (defaultSequence || [])
      }
    };

    // add existing saved cases
    if (currentRTFO?.fracSimCases && Object.keys(currentRTFO?.fracSimCases)?.length > 0) {
      console.log('Adding saved frac sim cases');
      Object.assign(fracSimCases, currentRTFO?.fracSimCases);
    };

    return fracSimCases;
  });

  const [currentFracSimCase, setCurrentFracSimCase] = useState(!isEmptySequence ? 'Actual Sequence' : 'Sequential');
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(() => fracSimCases[currentFracSimCase]?.pumpingSequence);

  const allStages = useMemo(() => {
    try {

      const rows = fracSimCases[currentFracSimCase]?.pumpingSequence;

      const allStagesObj = {};

      for (const rowIndex in rows) {
        const row = rows[rowIndex];

        const wellNames = Object.keys(row);

        for (const wellName of wellNames) {
          const stageNumber = parseInt(rowIndex) + 1;

          if (currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[String(stageNumber)] && +row[wellName] > 0) {
            const stageName = `${currentRTFO?.name}_${wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1,))}_${stageNumber}`;
            const numberInSequence = String(row[wellName]);

            allStagesObj[numberInSequence] = stageName;
          };
        };
      };

      // Step 1: Create an array of key-value pairs
      const keyValuePairs = Object.entries(allStagesObj);

      // Step 2: Sort the array based on keys
      keyValuePairs.sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

      // Step 3: Extract the sorted values
      const allSortedStages = keyValuePairs.map(pair => pair[1]);

      return allSortedStages;

    } catch (error) {
      return [];
    };
  }, [currentRTFO?.perfSchedule?.wells, currentRTFO?.name, fracSimCases, currentFracSimCase]);

  const remainingStages = useMemo(() => {
    try {

      const rows = defaultSequence;

      const remainingStagesObj = {};

      for (const rowIndex in rows) {
        const row = rows[rowIndex];

        const wellNames = Object.keys(row);

        for (const wellName of wellNames) {
          const stageNumber = parseInt(rowIndex) + 1;

          const stageName = `${currentRTFO?.name}_${wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1,))}_${stageNumber}`;
          if (currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[String(stageNumber)] && +row[wellName] > 0 && !allStages?.includes(stageName)) {
            const numberInSequence = String(row[wellName]);

            remainingStagesObj[numberInSequence] = stageName;
          };
        };
      };

      // Step 1: Create an array of key-value pairs
      const keyValuePairs = Object.entries(remainingStagesObj);

      // Step 2: Sort the array based on keys
      keyValuePairs.sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

      // Step 3: Extract the sorted values
      const remainingSortedStages = keyValuePairs.map(pair => pair[1]) || [];

      return remainingSortedStages;

    } catch (error) {
      return [];
    };
  }, [defaultSequence, currentRTFO?.name, currentRTFO?.perfSchedule?.wells, allStages]);

  const combinedStages = useMemo(() => {
    return [...allStages, ...remainingStages];
  }, [allStages, remainingStages])

  const [currentStageName, setCurrentStageName] = useState(allStages?.[allStages?.length - 1]);

  const currentWellName = currentStageName?.split('_')?.[1]?.toLowerCase();

  const [interactiveSequence, setInteractiveSequence] = useState(allStages || []);


  useEffect(() => {
    if (isPlayblast) {
      console.log('Starting playblast...');
      const interval = setInterval(() => {
        const currentIndex = allStages?.indexOf(currentStageName);
        if (currentIndex >= 0 && currentIndex < allStages?.length - 1) {
          setCurrentStageName(allStages?.[currentIndex + 1]);
        } else if (currentIndex === allStages?.length - 1) {
          setIsPlayblast(false);
          clearInterval(interval)
        };

      }, 1000);
      return () => clearInterval(interval);
    };
  }, [allStages, currentStageName, isPlayblast]);

  useEffect(() => {
    setInteractiveSequence(allStages || []);
    setCurrentStageName(allStages?.[allStages?.length - 1] || "");
  }, [currentFracSimCase, allStages]);

  const headerRow = {
    rowId: "header",
    cells: [{ type: "header", text: 'Stage' }, ...wellNames?.sort()?.map(w => ({ type: "header", text: w?.charAt(0)?.toUpperCase()?.concat(w?.substring(1,)) }))]
  };

  const getColumns = () => [
    { columnId: 'stage', width: 75 }, ...wellNames?.sort()?.map(w => ({ columnId: w, width: 100 }))
  ];

  const getRows = (data) => {
    const rows = [headerRow];

    data?.forEach((row, i) => {
      const rowCells = [{ type: "number", value: parseInt(i) + 1 }, ...Object.keys(row)?.map((d, j) => ({ type: "number", value: row[d] }))];
      rows.push({ rowId: i, cells: rowCells });
    });

    return rows;
  };

  const rows = getRows(data);
  const columns = getColumns();

  const handleChanges = (changes) => {
    try {
      const newData = JSON.parse(JSON.stringify(data));

      changes?.forEach((change) => {
        const dataIndex = change.rowId;
        const fieldName = change.columnId;
        newData[dataIndex][fieldName] = change.newCell.value;
      });

      // Check for duplicates
      const hasDuplicates = hasDuplicateValues(newData);

      if (hasDuplicates) {
        setIsError(true);
      } else {
        isError && setIsError(false);
      };

      setData(() => newData);
      setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...fracSimCases[currentFracSimCase], pumpingSequence: newData } }));
      setIsCaseEdited(true);

    } catch (error) {
      console.log(error);
      return;
    };

    function hasDuplicateValues(array) {
      try {
        const seenValues = {};

        const keys = Object.keys(array?.[0]);

        for (const obj of array) {
          for (const key of keys) {
            const value = obj[key];

            if (isNaN(value) || !value > 0) continue;

            if (seenValues[value]) {
              return true;
            };

            seenValues[value] = true;
          };

        };

        return false; // No duplicates found

      } catch (error) {
        console.log(error);
        return false;
      };
    };
  };

  const handleContextMenu = (
    selectedRowIds,
    selectedColIds,
    selectionMode,
    menuOptions
  ) => {

    if (selectionMode === "row") {
      menuOptions = [
        ...menuOptions,
        {
          id: "removeStage",
          label: "Remove stage",
          handler: () => {
            setData(prevData => {
              return [...prevData.filter((row, idx) => !selectedRowIds.includes(idx))]
            })
          }
        },
        {
          id: "addStage",
          label: "Add stage",
          handler: () => {
            setData(prevData => {
              const rowValues = {};
              for (const wellName of wellNames) {
                rowValues[wellName] = null;
              };
              return [...prevData, rowValues];
            })
          }
        }
      ];
    };
    return menuOptions;
  };

  const wellCoordinates = useMemo(() => {
    try {
      const coords = {};
      const wells = currentRTFO?.surveys?.wells && Object.keys(currentRTFO?.surveys?.wells);

      if (!wells?.length) return coords;

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = currentRTFO?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellCoordinates = surveyToCoordinates(lat, lon, surveyData);

        if (!coords[wells[i]]) coords[wells[i]] = [];

        coords[wells[i]] = wellCoordinates;
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };
  }, [currentRTFO?.surveys?.wells]);

  const clusterCoordinates3D = useMemo(() => {
    try {
      const coords = {};

      for (let j = 0; j < combinedStages?.length; j++) { // calculate stage/cluster lines

        const stageName = combinedStages[j];
        const stageNumber = String(stageName?.split('_')?.[2])?.replace(/[^0-9]+/g, '');
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        if (!currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]) continue;

        const fracLength = (fracSimCases?.[currentFracSimCase]?.fracHalfLength > 0 ? fracSimCases?.[currentFracSimCase]?.fracHalfLength : 300) * 2 * fracLengthDisplay;

        const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters ? Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters) : [];

        for (let k = 0; k < clusters?.length; k++) {
          const clusterName = clusters[k];
          const md = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);
          const clusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, md);
          let clusterLineCoordinates = calculateLineCoordinates(clusterLatLon, fracLength);
          clusterLineCoordinates = rotatePolyline(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

          if (!coords[stageName]) coords[stageName] = {};

          coords[stageName][clusterName] = {
            top: { // top of the frac plane
              start: { // start of the frac line along the plane top
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              center: { // center of the frac line along the plane top
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              end: { // end of the frac line along the plane top
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              }
            },
            center: { // center of the frac plane (corresponds to stage TVD)
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md)
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md)
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md)
              }
            },
            bottom: {
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              }
            }
          };
        };
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [combinedStages, currentRTFO?.settings, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, wellCoordinates, fracLengthDisplay, fracSimCases, currentFracSimCase]);

  const clusterCoordinatesSS = useMemo(() => {
    try {
      const coords = {};

      for (let j = 0; j < combinedStages?.length; j++) { // calculate stage/cluster lines

        const stageName = combinedStages[j];
        const stageNumber = String(stageName?.split('_')?.[2])?.replace(/[^0-9]+/g, '');
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        if (!currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]) continue;

        const fracLength = (fracSimCases?.[currentFracSimCase]?.fracHalfLength > 0 ? fracSimCases?.[currentFracSimCase]?.fracHalfLength : 300);

        const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters ? Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters) : [];

        for (let k = 0; k < clusters?.length; k++) {
          const clusterName = clusters[k];
          const md = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);
          const clusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, md);
          let clusterLineCoordinates = calculateLineCoordinates(clusterLatLon, fracLength);
          clusterLineCoordinates = rotatePolyline(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

          if (!coords[stageName]) coords[stageName] = {};

          coords[stageName][clusterName] = {
            top: { // top of the frac plane
              start: { // start of the frac line along the plane top
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              center: { // center of the frac line along the plane top
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              end: { // end of the frac line along the plane top
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              }
            },
            center: { // center of the frac plane (corresponds to stage TVD)
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md)
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md)
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md)
              }
            },
            bottom: {
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              }
            }
          };
        };
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [combinedStages, currentRTFO?.settings, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, wellCoordinates, fracSimCases, currentFracSimCase]);

  const stageCoordinates3D = useMemo(() => {
    try {
      const coords = {};

      Object.keys(clusterCoordinates3D)?.forEach(stageName => {
        const clusterCoords = Object.values(clusterCoordinates3D[stageName]);

        const firstCluster = clusterCoords?.[0]?.center; // coordinates of the first stage cluster at center tvd
        const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center; // coordinates of the last stage cluster at center tvd
        const startLatLon = [[firstCluster?.start?.lat, firstCluster?.start?.lon], [firstCluster?.center?.lat, firstCluster?.center?.lon], [firstCluster?.end?.lat, firstCluster?.end?.lon]];
        const endLatLon = [[lastCluster?.start?.lat, lastCluster?.start?.lon], [lastCluster?.center?.lat, lastCluster?.center?.lon], [lastCluster?.end?.lat, lastCluster?.end?.lon]];

        const averageTVD = (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2;

        const stageLatLon = getMiddlePoint([startLatLon, endLatLon]); // coordinates of the center of the stage

        const fracLength = (fracSimCases?.[currentFracSimCase]?.fracHalfLength > 0 ? fracSimCases?.[currentFracSimCase]?.fracHalfLength : 300) * 2 * fracLengthDisplay;

        let stageLineCoordinates = calculateLineCoordinates(stageLatLon, fracLength);
        stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

        coords[stageName] = {
          top: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            }
          },
          center: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD
            }
          },
          bottom: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            }
          }
        };
      });

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [clusterCoordinates3D, currentRTFO?.settings, fracLengthDisplay, fracSimCases, currentFracSimCase]);

  const stageCoordinatesSS = useMemo(() => {
    try {
      const coords = {};

      Object.keys(clusterCoordinatesSS)?.forEach(stageName => {
        const clusterCoords = Object.values(clusterCoordinatesSS[stageName]);

        const firstCluster = clusterCoords?.[0]?.center; // coordinates of the first stage cluster at center tvd
        const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center; // coordinates of the last stage cluster at center tvd
        const startLatLon = [[firstCluster?.start?.lat, firstCluster?.start?.lon], [firstCluster?.center?.lat, firstCluster?.center?.lon], [firstCluster?.end?.lat, firstCluster?.end?.lon]];
        const endLatLon = [[lastCluster?.start?.lat, lastCluster?.start?.lon], [lastCluster?.center?.lat, lastCluster?.center?.lon], [lastCluster?.end?.lat, lastCluster?.end?.lon]];

        const averageTVD = (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2;

        const stageLatLon = getMiddlePoint([startLatLon, endLatLon]); // coordinates of the center of the stage

        const fracLength = (fracSimCases?.[currentFracSimCase]?.fracHalfLength > 0 ? fracSimCases?.[currentFracSimCase]?.fracHalfLength : 300); // this is half of the hydraulic length

        let stageLineCoordinates = calculateLineCoordinates(stageLatLon, fracLength);
        stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

        coords[stageName] = {
          top: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            }
          },
          center: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD
            }
          },
          bottom: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            }
          }
        };
      });

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [clusterCoordinatesSS, currentRTFO?.settings, fracSimCases, currentFracSimCase]);

  const fpzCoordinates = useMemo(() => {
    try {
      if (!wellCoordinates || Object.keys(wellCoordinates)?.length === 0) return []

      const fpzCoord = []
      const buffers = []

      for (const wellName of Object.keys(wellCoordinates)) {
        for (const point of wellCoordinates[wellName]) {
          const center = [point?.lon, point?.lat];
          const radius = 1;
          const options = { steps: 64, units: 'kilometers' };
          const circle = turf.circle(center, radius, options);
          buffers.push(circle);
        }
      }

      // Combine all circle geometries
      const combinedCircles = turf.combine(turf.featureCollection(buffers));

      // Create a convex hull from the combined circles
      const convexHull = turf.convex(combinedCircles);

      for (const point of convexHull?.geometry?.coordinates?.[0]) {
        fpzCoord.push([point[1], point[0]])
      }

      return fpzCoord;

    } catch (error) {
      console.log(error)
    }

  }, [wellCoordinates]);

  // STRESS SHADOW WORKFLOW
  const stressShadow = useMemo(() => {
    try {
      let ss = {};

      const currentStageIndex = combinedStages?.indexOf(currentStageName);
      const previousStages = combinedStages?.slice(0, currentStageIndex + 1);

      for (let i = 0; i < previousStages?.length; i++) {
        const stageName = combinedStages[i];
        // iterate through all stages and calculate shadow for each stage within the radius
        ss = calculateStressShadow(
          currentRTFO?.stageData,
          combinedStages,
          ss,
          stageCoordinatesSS,
          clusterCoordinatesSS,
          stageName,
          currentRTFO?.perfSchedule,
          currentRTFO?.settings,
          currentRTFO?.formation,
          fracSimCases?.[currentFracSimCase]?.youngModulus,
          fracSimCases?.[currentFracSimCase]?.fracWidth,
          fracSimCases?.[currentFracSimCase]?.t
        );
      };

      return ss;

    } catch (error) {
      console.log(error);
    };

  }, [currentStageName, clusterCoordinatesSS, currentRTFO?.stageData, currentRTFO?.formation, currentRTFO?.perfSchedule, currentRTFO?.settings, stageCoordinatesSS, combinedStages, fracSimCases, currentFracSimCase]);

  // CALCULATE INTERSECTION LINE COORDINATES
  const stageIntersectionLineCoordinates = useMemo(() => {

    if (!currentStageName) return [];

    const stageLatLon = [stageCoordinates3D?.[currentStageName]?.center?.center?.lat, stageCoordinates3D?.[currentStageName]?.center?.center?.lon];
    let stageLineCoordinates = calculateLineCoordinates(stageLatLon, 5000);
    stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45)); // use 5 km line to find intersections

    return stageLineCoordinates;
  }, [stageCoordinates3D, currentStageName, currentRTFO?.settings?.fracAngle]);

  // CALCULATE GLOBAL MAP CENTER
  const globalCenter = useMemo(() => {
    try {
      return calculateCenterPoint(Object.keys(stageCoordinates3D)?.map(s => stageCoordinates3D[s]?.center?.center)?.flat(Infinity));
    } catch (error) {
      console.log(error);
      return { lat: 0, lon: 0 };
    };
  }, [stageCoordinates3D]);


  const stagePolygonsCoordinates = useMemo(() => {
    try {

      const allPolygonsCoordinates = {};

      for (const stageName in clusterCoordinates3D) {

        const stageClusters = Object.keys(clusterCoordinates3D?.[stageName]);
        const firstCluster = stageClusters?.[0];
        const lastCluster = stageClusters?.[stageClusters?.length - 1];

        if (firstCluster !== lastCluster) { // if multi-cluster stage
          // Top start, top end, bottom end, bottom start (swap lon and lat)
          const stagePolygonCoordinates = [
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lon],
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lon]
          ];
          allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
        } else { // if single-cluster stage
          const trimmedStageNumber = String(Number(String(stageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
          const wellName = stageName?.split('_')?.[1]?.toLowerCase();
          const lastStageNumber = Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0];
          const currentStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters?.['1']?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);

          let stageLength = 0;

          if (trimmedStageNumber !== lastStageNumber) {
            const nextStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) + 1]?.clusters?.['1']?.shotDepth;
            stageLength = currentStageDepth - nextStageDepth;
          } else { // for the last stage
            const previousStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) - 1]?.clusters?.['1']?.shotDepth;
            stageLength = previousStageDepth - currentStageDepth;
          };

          const fracLength = (fracSimCases?.[currentFracSimCase]?.fracHalfLength > 0 ? fracSimCases?.[currentFracSimCase]?.fracHalfLength : 300) * 2 * fracLengthDisplay;

          if (stageLength > 0) {
            // For bottom pseudo-cluster line
            const firstClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth - stageLength / 3);
            let firstClusterLineCoordinates = calculateLineCoordinates(firstClusterLatLon, fracLength);
            firstClusterLineCoordinates = rotatePolyline(firstClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

            // For top pseudo-cluster line
            const lastClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth + stageLength / 3);
            let lastClusterLineCoordinates = calculateLineCoordinates(lastClusterLatLon, fracLength);
            lastClusterLineCoordinates = rotatePolyline(lastClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

            const stagePolygonCoordinates = [
              [firstClusterLineCoordinates?.[0]?.[0], firstClusterLineCoordinates?.[0]?.[1]],
              [firstClusterLineCoordinates?.[2]?.[0], firstClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[2]?.[0], lastClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[0]?.[0], lastClusterLineCoordinates?.[0]?.[1]]
            ];
            allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
          };

        };

      };

      return allPolygonsCoordinates;

    } catch (error) {
      console.log(error);
      return null;
    };
  }, [clusterCoordinates3D, wellCoordinates, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, currentRTFO?.settings?.fracAngle, fracLengthDisplay, fracSimCases, currentFracSimCase]);

  const legendColorRange = useMemo(() => {

    const allSSValues = Object.values(stressShadow);
    const min = Math.min(...allSSValues) * (units === 'field' ? rtfoConversionFactors.pressure : 1);
    const max = Math.max(...allSSValues) * (units === 'field' ? rtfoConversionFactors.pressure : 1);

    return [min, max];
  }, [stressShadow, units]);

  const stageColors = useMemo(() => {
    try {

      const allStagesColors = {};

      for (const stageName in stagePolygonsCoordinates) {
        const value = (stressShadow?.[stageName] || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1);

        const colorScale = scaleSequential(interpolateTurbo).domain([legendColorRange?.[0], legendColorRange?.[1]]);
        let color = colorScale(value);

        if (!allStages.includes(stageName)) {
          color = 'grey';
        };

        allStagesColors[stageName] = color;
      };

      return allStagesColors;
    } catch (error) {
      console.log(error);
      return '';
    };
  }, [stagePolygonsCoordinates, stressShadow, units, legendColorRange, allStages]);


  try {
    return (
      <Paper style={{ margin: '0 20px', padding: 0, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')], position: 'relative', display: 'flex', flexDirection: 'column' }} elevation={0} sx={{ boxShadow: 1 }}>

        <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'row' }}>


          <div style={{ position: 'relative', height: '100%', width: 'calc(100% - 500px)', outline: interactiveMode && '4px solid #f44336', zIndex: 420 }}>
            {!interactiveMode && <div style={{ width: 'calc(100% - 150px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'absolute', top: 10, left: 10, zIndex: 419 }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography fontSize="12px">Hydraulic Half Length Display:</Typography>
                <FormControl style={{ marginLeft: 10 }}>
                  <Select
                    value={fracLengthDisplay}
                    onChange={(e) => setFracLengthDisplay(e.target.value)}
                    size="small"
                    style={{ width: 100, height: 28, backgroundColor: !darkMode && '#fff', fontSize: '12px' }}
                  >
                    <MenuItem value={1}>100%</MenuItem>
                    <MenuItem value={0.5}>50%</MenuItem>
                    <MenuItem value={0.25}>25%</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginRight: 20 }}>
                <div>
                  <Button
                    startIcon={<FastRewind />}
                    onClick={() => {
                      setCurrentStageName(allStages?.[0]);
                    }}
                    color={darkMode ? "inherit" : "primary"}
                    size="small"
                    style={{ marginRight: 10, height: 28, fontSize: '12px' }}
                    variant="contained"
                  >First</Button>
                  <Button
                    startIcon={<SkipPrevious />}
                    onClick={() => {
                      const currentIndex = allStages?.indexOf(currentStageName);
                      if (currentIndex > 0) {
                        setCurrentStageName(allStages?.[currentIndex - 1]);
                      };
                    }}
                    color={darkMode ? "inherit" : "primary"}
                    size="small"
                    style={{ marginRight: 10, height: 28, fontSize: '12px' }}
                    variant="contained"
                  >Previous</Button>
                  <Button
                    startIcon={isPlayblast ? <Stop /> : <PlayArrow />}
                    onClick={() => setIsPlayblast(prev => !prev)}
                    color={darkMode ? "inherit" : "primary"}
                    size="small"
                    style={{ marginRight: 10, height: 28, fontSize: '12px' }}
                    variant="contained"
                  >{isPlayblast ? 'Stop' : 'Start'}</Button>
                  <Button
                    startIcon={<SkipNext />}
                    onClick={() => {
                      const currentIndex = allStages?.indexOf(currentStageName);
                      if (currentIndex >= 0 && currentIndex < allStages?.length - 1) {
                        setCurrentStageName(allStages?.[currentIndex + 1]);
                      };
                    }}
                    color={darkMode ? "inherit" : "primary"}
                    size="small"
                    style={{ marginRight: 10, height: 28, fontSize: '12px' }}
                    variant="contained"
                  >Next</Button>
                  <Button
                    startIcon={<FastForward />}
                    onClick={() => {
                      setCurrentStageName(allStages?.[allStages?.length - 1]);
                    }}
                    color={darkMode ? "inherit" : "primary"}
                    size="small"
                    style={{ marginRight: 10, height: 28, fontSize: '12px' }}
                    variant="contained"
                  >Last</Button>
                </div>
              </div>

            </div>}

            {interactiveMode &&
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', zIndex: 421, backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: 10 }}>
                <Typography style={{ color: 'black' }}>{`Click to set stage # ${interactiveSequence?.length + 1} in the sequence`}</Typography>
                <div>
                  <Button startIcon={<Cancel />} size="small" style={{ height: 28 }} onClick={() => {
                    setInteractiveMode(false);
                    handleSequenceReset();
                  }}>
                    Cancel
                  </Button>
                  <Button startIcon={<RestartAlt />} size="small" style={{ height: 28, marginLeft: 10 }} onClick={() => {
                    handleSequenceReset();
                    // setInteractiveSequence(allStages || []);
                  }}>
                    Reset
                  </Button>
                  <Button startIcon={<Check />} size="small" style={{ height: 28, marginLeft: 10 }} onClick={handleInteractiveSequenceSave}>Apply</Button>
                </div>
              </div>
            }

            <MapContainer center={[globalCenter?.lat, globalCenter?.lon]} zoom={14} maxZoom={16} ref={mapRef} style={{ height: 900, position: 'relative', backgroundColor: darkMode ? 'black' : 'white' }} attributionControl={true} zoomControl={false} scrollWheelZoom={false}>

              {darkMode ?
                <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`} /> :
                <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`} />}
              {/* <TileLayer url={`https://tile.openstreetmap.org/{z}/{x}/{y}.png`} removeAttribution attributionControl="false" /> */}

              {fpzCoordinates && <Polygon positions={fpzCoordinates} pathOptions={{ fillOpacity: 0.25, color: 'transparent', fillColor: 'grey' }} />}
              {Object.keys(wellCoordinates)?.map((well, index) => {
                return (
                  <div key={`${well}-${index}`}>
                    <Polyline pathOptions={{ color: well === currentStageName?.split('_')?.[1]?.toLowerCase() ? 'red' : 'black', weight: 6 }} positions={wellCoordinates[well]?.map(w => [w.lat, w.lon])} bounds={L.latLngBounds(wellCoordinates[well]?.map(w => [w.lat, w.lon]))} />
                    <CircleMarker center={[currentRTFO?.surveys?.wells?.[well]?.surfaceLat, currentRTFO?.surveys?.wells?.[well]?.surfaceLon]} pathOptions={{ color: 'black' }} radius={10}>
                    </CircleMarker>
                    <WellLabel 
                      center={[wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lon, wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lat]} 
                      wellName={well} 
                      currentWellName={currentStageName?.split('_')?.[1]?.toLowerCase()} 
                      wellAlias={currentRTFO?.wellAlias?.[well]} 
                      darkMode={darkMode}
                    />

                    {/* <CircleMarker center={wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]} pathOptions={{ color: 'black' }} radius={1}>
                      <Tooltip direction={"right"} offset={[0, 0]} opacity={1} permanent>
                        {`${well?.charAt(0)?.toUpperCase()+well.slice(1,)}`}
                      </Tooltip>
                    </CircleMarker> */}
                  </div>
                )
              })}

              {!interactiveMode ?
                Object.keys(stagePolygonsCoordinates)?.map(stageName => {
                  return (
                    stagePolygonsCoordinates[stageName] ?
                      <Polygon positions={stagePolygonsCoordinates[stageName]} pathOptions={{ fillColor: stageColors?.[stageName], fillOpacity: 0.75, color: 'transparent' }} key={stageName} /> : <div key={stageName}></div>
                  );
                }) :
                Object.keys(stagePolygonsCoordinates)?.map((stageName, index) => {
                  return (
                    stageCoordinates3D[stageName] ?
                      <CircleMarker
                        key={stageName}
                        center={[stageCoordinates3D[stageName]?.center?.center?.lat, stageCoordinates3D[stageName]?.center?.center?.lon]}
                        pathOptions={{ color: interactiveSequence?.indexOf(stageName) === -1 ? 'red' : 'green' }}
                        radius={8}
                        eventHandlers={{
                          click: () => {
                            if (interactiveSequence?.indexOf(stageName) === -1) {
                              setInteractiveSequence(prev => [...prev]?.concat(stageName));
                            } else {
                              setInteractiveSequence(prev => prev?.filter(s => s !== stageName));
                            };

                          }
                        }}
                      >
                        <StageLabel 
                          key={stageName + '-label'} 
                          coord={stageCoordinates3D[stageName]?.center} 
                          stageName={stageName} 
                          numberInSequence={interactiveSequence?.indexOf(stageName) + 1} 
                          interactive={true} 
                          darkMode={darkMode}
                        />
                      </CircleMarker>
                      : <div key={stageName}></div>
                  );
                })}


              {!interactiveMode && allStages?.map((stageName, index) => {
                return (
                  stageCoordinates3D[stageName] ?
                    <StageLabel 
                      key={stageName} 
                      coord={stageCoordinates3D[stageName]?.center} 
                      stageName={stageName} 
                      numberInSequence={parseInt(index) + 1} 
                      darkMode={darkMode}
                    /> : <div key={stageName}></div>
                );
              })}

              {!interactiveMode && !isNaN(stageIntersectionLineCoordinates?.[0]?.[0]) && !isNaN(stageIntersectionLineCoordinates?.[1]?.[0]) ?
                <Polyline pathOptions={{ color: chartColors[0], weight: 2 }} positions={stageIntersectionLineCoordinates} bounds={L.latLngBounds(stageIntersectionLineCoordinates)}>
                </Polyline> : <></>}


              {!interactiveMode && Object.keys(stageCoordinates3D)?.map(stageName => {
                return Object.keys(clusterCoordinates3D[stageName])?.map((cluster, cIndex) => {
                  const clusterLineCoordinates = [
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.start?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.start?.lon],
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.center?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.center?.lon],
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.end?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.end?.lon]
                  ];
                  return (
                    !isNaN(clusterLineCoordinates?.[0]?.[0]) && !isNaN(clusterLineCoordinates?.[1]?.[0]) && !isNaN(clusterLineCoordinates?.[2]?.[0]) ?
                      <Polyline pathOptions={{ weight: stageName === currentStageName ? 2 : 1, color: stageName === currentStageName ? 'red' : stageColors?.[stageName], lineCap: 'butt' }} positions={clusterLineCoordinates} bounds={L.latLngBounds(clusterLineCoordinates)} key={`${stageName}-${cluster}-${cIndex}`}>
                        {/* <Polyline pathOptions={{ weight: stageName === currentStageName ? 2 : 1, color: stageName === currentStageName ? 'red' : 'black', lineCap: 'butt' }} positions={clusterLineCoordinates} bounds={L.latLngBounds(clusterLineCoordinates)} key={`${stageName}-${cluster}-${cIndex}`}> */}
                        <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction="bottom" offset={[0, 0]} opacity={1}>
                          <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], color: darkMode ? 'white' : 'black', padding: 4 }}>
                            <Typography textAlign={'center'} fontSize="10px">{`${stageName?.split('_')?.[1]} - ${stageName?.split('_')?.[2]}`}</Typography>
                          </div>
                        </Tooltip>
                      </Polyline>
                      :
                      <div key={cIndex}></div>
                  );
                });
              })}

              {!interactiveMode && <RTFOMapLegend units={units} darkMode={darkMode} minColorValue={legendColorRange?.[0]} maxColorValue={legendColorRange?.[1]} metric={'Stress Shadow'} showMetric={true} />}

              <ScrollZoomControl setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} darkMode={darkMode} />

              {scrollZoomControlOverlayVisible && <ScrollZoomControlOverlay setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} />}
            </MapContainer>
          </div>


          <div style={{ height: 900, width: 500, borderLeft: '1px solid grey', padding: 10, overflow: 'scroll', outline: isError && '4px solid #f44336', zIndex: 666, position: 'relative' }}>

            {isError && <Typography style={{ position: 'absolute', top: 0, color: 'red', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>Duplicate values detected!</Typography>}

            <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" style={{ marginBottom: 10 }}>Simulator Settings</Typography>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flexStart', marginBottom: 10 }}>
              <Typography>Simulator Case</Typography>
              <FormControl>
                <Select
                  value={currentFracSimCase}
                  onChange={(e) => handleFracSimCaseChange(e)}
                  size="small"
                  style={{ width: '100%', height: 28 }}
                  fullWidth
                >
                  {Object.keys(fracSimCases)?.map(caseName => (
                    <MenuItem key={caseName} value={caseName}>
                      <ListItemText primary={caseName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
                <Button variant="outlined" size="small" color="secondary" style={{ height: '28px' }} onClick={handleDeleteFracSimCase} startIcon={<Delete />} disabled={currentFracSimCase === 'Actual Sequence' || interactiveMode} title="Delete Simulator Case">Delete</Button>
                <Button
                  variant="outlined"
                  size="small"
                  style={{
                    height: '28px',
                    color: (darkMode ? '#fff' : theme.palette.primary.main),
                    borderColor: (darkMode ? '#fff' : theme.palette.primary.main)
                  }}
                  onClick={handleSequenceReset}
                  startIcon={<RestartAlt />}
                  title="Reset Simulator Settings"
                  disabled={interactiveMode || currentFracSimCase === 'Actual Sequence'}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  style={{
                    height: '28px',
                    color: (darkMode ? '#fff' : theme.palette.primary.main),
                    borderColor: (darkMode ? '#fff' : theme.palette.primary.main)
                  }}
                  onClick={handleDuplicateFracSimCase}
                  startIcon={<ContentCopy />}
                  title="Duplicate Simulator Case"
                  disabled={interactiveMode}
                >
                  Duplicate
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  style={{
                    height: '28px',
                    color: (darkMode ? '#fff' : theme.palette.primary.main),
                    borderColor: (darkMode ? '#fff' : theme.palette.primary.main)
                  }}
                  onClick={handleAddNewFracSimCase}
                  startIcon={<Add />}
                  title="Add New Simulator Case"
                  disabled={interactiveMode}
                >
                  New
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  style={{
                    height: '28px',
                    color: darkMode ? '#000' : '#fff',
                    backgroundColor: darkMode ? "#fff" : theme.palette.primary.main
                  }}
                  onClick={handleFracSimCasesSave}
                  disabled={isError || !isCaseEdited || interactiveMode || currentFracSimCase === 'Actual Sequence'}
                  startIcon={<Save />}
                  title="Save Simulator Case"
                >
                  Save
                </Button>
              </div>
            </div>

            <Divider />


            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20, marginTop: 10 }}>
              <Button
                variant="contained"
                size="small"
                style={{ height: '28px', marginRight: 10, color: darkMode ? '#000' : '#fff', backgroundColor: darkMode ? "#fff" : theme.palette.primary.main }}
                onClick={() => {
                  setInteractiveMode(true);
                  // handleSequenceReset();
                  // setInteractiveSequence([]);
                  // setModifiedData([]);
                  // setData([]);
                }}
                startIcon={<AdsClick />}
                title="Enable Interactive Mode"
                disabled={interactiveMode || currentFracSimCase === 'Actual Sequence'}
              >
                Interactive
              </Button>
              <div>
                {/* <Button variant="contained" size="small" style={{height: '28px'}} onClick={handleSequenceRecalculate} disabled={isError || interactiveMode} startIcon={<Check />} title="Recalculate Simulator Sequence">Recalculate</Button> */}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 20, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
                <Typography style={{ marginRight: 10 }} variant="caption">Half Length</Typography>
                <TextField
                  variant="outlined"
                  value={((fracSimCases?.[currentFracSimCase]?.fracHalfLength * (units === 'field' ? rtfoConversionFactors.halfLength : 1)))}
                  onChange={(e) => {
                    const newHalfLength = ((e.target.value / (units === 'field' ? rtfoConversionFactors.halfLength : 1)));
                    setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...prev[currentFracSimCase], fracHalfLength: newHalfLength } }));
                    setIsCaseEdited(true);
                  }}
                  size="small"
                  style={{ width: 100, height: '28px' }}
                  inputProps={{ style: { height: '28px' } }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{units === 'field' ? 'ft' : 'm'}</InputAdornment>,
                    style: { fontSize: 14, height: '28px' }
                  }}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
                <Typography style={{ marginRight: 10 }} variant="caption">Young's Modulus</Typography>
                <TextField
                  variant="outlined"
                  value={fracSimCases?.[currentFracSimCase]?.youngModulus}
                  onChange={(e) => {
                    setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...prev[currentFracSimCase], youngModulus: e.target.value } }));
                    setIsCaseEdited(true);
                  }}
                  size="small"
                  style={{ width: 125, height: '28px' }}
                  inputProps={{ style: { height: '28px' } }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">psi</InputAdornment>,
                    style: { fontSize: 14, height: '28px' }
                  }}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
                <Typography style={{ marginRight: 10 }} variant="caption">Frac Width</Typography>
                <TextField
                  variant="outlined"
                  value={fracSimCases?.[currentFracSimCase]?.fracWidth}
                  onChange={(e) => {
                    setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...prev[currentFracSimCase], fracWidth: e.target.value } }));
                    setIsCaseEdited(true);
                  }}
                  size="small"
                  style={{ width: 100, height: '28px' }}
                  inputProps={{ style: { height: '28px' } }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">in</InputAdornment>,
                    style: { fontSize: 14, height: '28px' }
                  }}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
                <Typography style={{ marginRight: 10 }} variant="caption">Transv. Exp.</Typography>
                <TextField
                  variant="outlined"
                  value={fracSimCases?.[currentFracSimCase]?.t}
                  onChange={(e) => {
                    setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...prev[currentFracSimCase], t: e.target.value } }));
                    setIsCaseEdited(true);
                  }}
                  size="small"
                  style={{ width: 100, height: '28px' }}
                  inputProps={{ style: { height: '28px' } }}
                  InputProps={{
                    style: { fontSize: 14, height: '28px' }
                  }}
                  onFocus={event => {
                    event.target.select();
                  }}
                />
              </div>
            </div>
            <ReactGrid
              divProps={{
                style: {
                  pointerEvents: currentFracSimCase === 'Actual Sequence' && 'none',
                  opacity: currentFracSimCase === 'Actual Sequence' && 0.5,
                  cursor: currentFracSimCase === 'Actual Sequence' && 'not-allowed'
                }
              }}
              rows={rows}
              columns={columns}
              onCellsChanged={handleChanges}
              enableRangeSelection
              enableRowSelection
              enableFillHandle
              stickyTopRows={1}
              stickyLeftColumns={1}
              onContextMenu={handleContextMenu}
              disabled
              style={{ color: 'white' }}
            />
          </div>

          {!interactiveMode && <div style={{ position: 'absolute', bottom: 0, left: 8, zIndex: 420 }}>
            <Typography fontSize='12px' style={{ position: 'absolute', left: '50%', top: 5, transform: 'translateX(-50%)' }}>Cross-Section View</Typography>
            <WellboreCrossSection
              currentStageName={currentStageName}
              wellCoordinates={wellCoordinates}
              stageCoordinates3D={stageCoordinates3D}
              clusterCoordinates3D={clusterCoordinates3D}
              stressShadow={stressShadow}
              stageData={currentRTFO?.stageData}
              settings={currentRTFO?.settings}
              surveyData={currentRTFO?.surveys?.wells?.[currentWellName?.toLowerCase()]?.surveyData}
              fracLengthDisplay={fracLengthDisplay}
              formation={currentRTFO?.formation}
              client={currentRTFO?.client}
              fracHalfLength={fracSimCases?.[currentFracSimCase]?.fracHalfLength}
              darkMode={darkMode}
            />
          </div>}
        </div>

        <div style={{ width: '100%', height: 540, borderTop: '1px solid grey' }}>
          <RTFOWellboreTrajectoryChart currentStageName={currentStageName} />
        </div>

        <div style={{ height: 900, width: '100%', borderTop: '1px solid grey' }}>
          <RTFOFracSimulatorDataTable stressShadow={stressShadow} allStages={allStages} units={units} darkMode={darkMode} />
        </div>

        {isAddNewFracSimCaseVisible &&
          <RTFOAddNewFracSimCase
            fracSimCases={fracSimCases}
            setFracSimCases={setFracSimCases}
            setData={setData}
            setCurrentFracSimCase={setCurrentFracSimCase}
            setIsCaseEdited={setIsCaseEdited}
            setIsAddNewFracSimCaseVisible={setIsAddNewFracSimCaseVisible}
            actualSequence={actualSequence}
            defaultSequence={defaultSequence}
            blankSequence={blankSequence}
            defaultModelParameters={defaultModelParameters}
          />}
      </Paper>
    );
  } catch (error) {
    console.log(error);
    return <></>;
  };




  function handleSequenceReset() {
    try {
      let caseSequenceSource = defaultSequence;

      if (fracSimCases[currentFracSimCase]?.source === 'Actual Sequence') {
        caseSequenceSource = actualSequence;
        // } else if (fracSimCases[currentFracSimCase]?.source === 'Blank') {
        //   caseSequenceSource = blankSequence;
      } else if (fracSimCases[currentFracSimCase]?.source === 'Sequential') {
        caseSequenceSource = defaultSequence;
      } else {
        caseSequenceSource = fracSimCases[currentFracSimCase]?.pumpingSequence || defaultSequence;
      };

      setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...fracSimCases[currentFracSimCase], pumpingSequence: caseSequenceSource } }));
      setIsCaseEdited(true);
      setData(caseSequenceSource);
      setInteractiveSequence(allStages || []);
      setCurrentStageName(allStages?.[allStages?.length - 1] || "");
      setIsError(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleInteractiveSequenceSave() {
    try {

      if (interactiveSequence?.length === 0) {
        handleSequenceReset();
        setInteractiveMode(false);
        return;
      };

      const allStageNumbers = Object.keys(currentRTFO?.perfSchedule?.wells)?.map(w => Object.keys(currentRTFO?.perfSchedule?.wells?.[w]?.stages)?.map(s => Number(s)))?.flat(Infinity);

      const maxStageNumber = Math.max(...allStageNumbers);

      const rows = [];

      // Construct an empty array with all stages
      for (let rowNumber = 0; rowNumber < maxStageNumber; rowNumber++) {
        const row = {};
        for (const wellName of wellNames) {
          row[wellName] = null;
        };
        rows.push(row);
      };

      // Add values for all assigned stages
      for (let rowNumber = 0; rowNumber < rows?.length; rowNumber++) {
        const row = rows?.[rowNumber];

        for (const wellName of wellNames) {
          // const formattedWellName = wellName?.toLowerCase();
          const formattedWellName = wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1,));
          const stageName = `${currentRTFO?.name}_${formattedWellName}_${rowNumber + 1}`;

          if (interactiveSequence?.indexOf(stageName) >= 0) {
            row[wellName] = interactiveSequence?.indexOf(stageName) + 1;
          };

        };
      };

      setFracSimCases(prev => ({ ...prev, [currentFracSimCase]: { ...fracSimCases[currentFracSimCase], pumpingSequence: rows } }));
      setIsCaseEdited(true);
      setData(rows);
      setIsError(false);
      setInteractiveMode(false);

    } catch (error) {

    };
  };

  // function handleSequenceRecalculate() {
  //   try {
  //     // setFracSimCases(prev => ({...prev, [currentFracSimCase]: {...fracSimCases[currentFracSimCase], pumpingSequence: data}}));
  //     // setIsCaseEdited(true);
  //   } catch (error) {
  //     console.log(error);
  //     return;
  //   };
  // };

  function handleFracSimCaseChange(e) {
    try {
      const newFracSimCase = fracSimCases?.[e.target.value];

      if (!newFracSimCase) return;

      setIsCaseEdited(true);
      setCurrentFracSimCase(e.target.value);
      setData(newFracSimCase?.pumpingSequence);
      // setInteractiveSequence([]);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDeleteFracSimCase() {
    try {
      if (currentFracSimCase === 'Actual Sequence') return;

      const newFracSimCases = { ...fracSimCases };
      delete newFracSimCases[currentFracSimCase];

      if (Object.keys(newFracSimCases)?.length === 0) return;

      setFracSimCases(newFracSimCases);
      setIsCaseEdited(true);
      setCurrentFracSimCase(Object.keys(newFracSimCases)?.[0]);
      setData(newFracSimCases?.[Object.keys(newFracSimCases)?.[0]]?.pumpingSequence);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleAddNewFracSimCase() {
    try {
      setIsAddNewFracSimCaseVisible(true);
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDuplicateFracSimCase() {
    try {
      const newFracSimCaseName = `Case ${Object.keys(fracSimCases)?.length + 1}`;
      const currentFracSimCaseData = fracSimCases[currentFracSimCase];

      const newFracSimCase = JSON.parse(JSON.stringify(currentFracSimCaseData));
      newFracSimCase.name = newFracSimCaseName;

      setIsCaseEdited(true);
      setFracSimCases(prev => ({ ...prev, [newFracSimCaseName]: newFracSimCase }));
      setData(newFracSimCase.pumpingSequence);
      setCurrentFracSimCase(newFracSimCaseName);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleFracSimCasesSave() {
    try {
      const newFracSimCases = JSON.parse(JSON.stringify(fracSimCases));
      delete newFracSimCases['Actual Sequence'];

      if (socket && socket.readyState === 1) {
        socket?.send(JSON.stringify({
          messageType: 'saveFracSimCases',
          padName: currentRTFO?.name,
          fracSimCases: newFracSimCases
        }));
      };

      setIsCaseEdited(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function StageLabel({ coord, stageName, numberInSequence, interactive, stageColor = "white", darkMode }) {
    try {
      const center = [coord?.center?.lon, coord?.center?.lat];
      const radius = 60 / 1000;
      const options = { steps: 16, units: 'kilometers' };
      const circle = turf.circle(center, radius, options);
      let coords = circle?.geometry?.['coordinates']?.[0]?.map(c => [c?.[1], c?.[0]]);

      const width = 90;
      const height = interactive ? 90 : 140;

      // Calculate x and y coordinates of the rectangle center
      const centerX = center?.[1];
      const centerY = center?.[0];

      // Calculate x and y coordinates of the top-left corner of the rectangle bounding box
      const x = centerX - width / 2;
      const y = centerY - height / 2;

      const wellName = stageName?.split('_')?.[1]?.toLowerCase();
      const stageNumber = stageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '');
      const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
      const clusterNames = stageClusters && Object.keys(stageClusters);
      const numClusters = clusterNames?.length || 0;

      return (
        <SVGOverlay
          bounds={L.latLngBounds(coords)}
          attributes={{
            viewBox: `${x} ${y} ${width} ${height}`
          }}
        >
          {/* <circle cx={centerX} cy={centerY} r="40" fill={stageColor} opacity={0.5} /> */}
          {numberInSequence > 0 && <text 
            x={centerX} 
            y={centerY - (interactive ? 0 : numClusters < 3 ? 8 : 15)} 
            textAnchor="middle" 
            dominantBaseline="middle" 
            strokeWidth="3px" 
            paintOrder="stroke" 
            stroke={!interactive ? "white" : darkMode ? "white" : "black"}
            fill={!interactive ? "black" : darkMode ? "black" : "white"}
            fontSize={interactive && numClusters < 3 ? '24px' : interactive && numClusters >= 3 ? '48px' : numClusters < 3 ? '16px' : '32px'} 
            fontWeight="bold"
          >
            {numberInSequence}
          </text>}
          {!interactive && <text 
            x={centerX} 
            y={centerY + (numClusters < 3 ? 8 : 15)} 
            textAnchor="middle" 
            dominantBaseline="middle" 
            strokeWidth="2px" 
            paintOrder="stroke" 
            stroke={darkMode ? "black" : "white"}
            fill={darkMode ? "white" : "black"}
            fontSize={numClusters < 3 ? '11px' : '24px'} 
            fontWeight="bold"
          >
            {((stressShadow[stageName] || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(1)}
          </text>}
        </SVGOverlay>
      );

    } catch (error) {
      return <></>
    };

  };
};

export default RTFOFracSimulator;

const RTFOAddNewFracSimCase = ({
  fracSimCases = {},
  setFracSimCases,
  setData,
  setCurrentFracSimCase,
  setIsAddNewFracSimCaseVisible,
  setIsCaseEdited,
  actualSequence,
  defaultSequence,
  blankSequence,
  defaultModelParameters
}) => {

  const [fracSimCaseName, setFracSimCaseName] = useState(`Case ${(fracSimCases ? Object.keys(fracSimCases)?.length : 0) + 1}`);
  const [fracSimCaseSource, setFracSimCaseSource] = useState('Actual Sequence');
  const [fracSimCaseModelParameters, setFracSimCaseModelParameters] = useState(defaultModelParameters);

  const isError = nameExists(fracSimCases, fracSimCaseName);
  const availableSources = ['Actual Sequence', 'Blank', 'Sequential', ...Object.keys(fracSimCases)?.filter(c => c !== 'Actual Sequence' && c !== 'Sequential')];
  return (
    <Dialog
      open={true}
      onClose={() => setIsAddNewFracSimCaseVisible(false)}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogTitle>New Simulator Case</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="normal"
          id="name"
          label="Simulator Case Name"
          placeholder="New Case"
          type="text"
          fullWidth
          value={fracSimCaseName}
          variant="outlined"
          onChange={e => setFracSimCaseName(e.target.value)}
          onFocus={event => {
            event.target.select();
          }}
          error={isError}
        />

        <FormControl style={{ marginTop: 20, marginBottom: 5, width: '100%' }}>
          <Typography variant="subtitle2">
            Simulator Case Sequence
          </Typography>
          <Select
            value={fracSimCaseSource}
            onChange={(e) => setFracSimCaseSource(e.target.value)}
            fullWidth
            style={{ width: '100%' }}
          >
            {availableSources?.map(source => {
              return (
                <MenuItem value={source} key={source}>{source}</MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 20, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
            <Typography style={{ marginRight: 10 }} variant="subtitle2">Half Length</Typography>
            <TextField
              variant="outlined"
              value={fracSimCaseModelParameters?.fracHalfLength}
              onChange={(e) => setFracSimCaseModelParameters(prev => ({ ...prev, fracHalfLength: e.target.value }))}
              size="small"
              style={{ width: 100 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
              onFocus={event => {
                event.target.select();
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
            <Typography style={{ marginRight: 10 }} variant="subtitle2">Young's Modulus</Typography>
            <TextField
              variant="outlined"
              value={fracSimCaseModelParameters?.youngModulus}
              onChange={(e) => setFracSimCaseModelParameters(prev => ({ ...prev, youngModulus: e.target.value }))}
              size="small"
              style={{ width: 125 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">psi</InputAdornment>,
              }}
              onFocus={event => {
                event.target.select();
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
            <Typography style={{ marginRight: 10 }} variant="subtitle2">Frac Width</Typography>
            <TextField
              variant="outlined"
              value={fracSimCaseModelParameters?.fracWidth}
              onChange={(e) => setFracSimCaseModelParameters(prev => ({ ...prev, fracWidth: e.target.value }))}
              size="small"
              style={{ width: 100 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">in</InputAdornment>,
              }}
              onFocus={event => {
                event.target.select();
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: 0 }}>
            <Typography style={{ marginRight: 10 }} variant="subtitle2">Transv. Exp.</Typography>
            <TextField
              variant="outlined"
              value={fracSimCaseModelParameters?.t}
              onChange={(e) => setFracSimCaseModelParameters(prev => ({ ...prev, t: e.target.value }))}
              size="small"
              style={{ width: 100 }}
              onFocus={event => {
                event.target.select();
              }}
            />
          </div>
        </div>

      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsAddNewFracSimCaseVisible(false)} color="primary" style={{ marginRight: 10 }}>
          Cancel
        </Button>
        <Button onClick={() => handleAddNewFracSimCase(fracSimCaseName, fracSimCaseSource)} color="primary" variant="contained" disabled={nameExists(fracSimCases, fracSimCaseName) || !fracSimCaseName?.length > 0}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );

  function nameExists(fracSimCases, fracSimCaseName) {
    if (fracSimCases && Object.keys(fracSimCases)?.includes(fracSimCaseName)) {
      return true;
    } else {
      return false;
    };
  };

  function handleAddNewFracSimCase() {
    try {
      let sequenceSource = [];

      if (fracSimCaseSource === 'Actual Sequence') {
        sequenceSource = actualSequence;
      } else if (fracSimCaseSource === 'Blank') {
        sequenceSource = blankSequence;
      } else if (fracSimCaseSource === 'Sequential') {
        sequenceSource = defaultSequence;
      };

      const newFracSimCase = {
        name: fracSimCaseName,
        source: fracSimCaseSource,
        fracHalfLength: fracSimCaseModelParameters.fracHalfLength,
        youngModulus: fracSimCaseModelParameters?.youngModulus,
        fracWidth: fracSimCaseModelParameters?.fracWidth,
        t: fracSimCaseModelParameters?.t,
        pumpingSequence: sequenceSource
      };

      setCurrentFracSimCase(fracSimCaseName);
      setFracSimCases(prev => ({ ...prev, [fracSimCaseName]: newFracSimCase }));
      setData(sequenceSource);
      setIsAddNewFracSimCaseVisible(false);
      setIsCaseEdited(true);

    } catch (error) {
      console.log(error);
    };
  };
}

function calculateStressShadow(stageData, allStages, ss, stageCoordinates, clusterCoordinates, currentStageName, perfSchedule, settings, formation, youngModulus, fracWidth, t) {
  try {

    let newSS = JSON.parse(JSON.stringify(ss));

    const shadowRadius = settings?.shadowRadius || 300; // meters

    const currentStageIndex = allStages?.indexOf(currentStageName);
    const stageNames = allStages?.slice(Math.min(currentStageIndex, allStages?.length),); // filter out stages before the currentStageName
    const stagesWithinRadius = {};

    if (!clusterCoordinates?.[currentStageName]) return ss;

    const currentStageClusters = Object.keys(clusterCoordinates?.[currentStageName]);
    const currentFirstClusterCoords = clusterCoordinates?.[currentStageName]?.[currentStageClusters?.[0]]; // coordinates of the first stage cluster
    const currentLastClusterCoords = clusterCoordinates?.[currentStageName]?.[currentStageClusters?.[currentStageClusters?.length - 1]]; // coordinates of the last stage cluster


    // find all stages within shadow radius of the current stage
    for (let stageName of stageNames) {

      if (!clusterCoordinates?.[stageName]) continue;

      const nextStageCenterCoordinates = stageCoordinates?.[stageName]?.center?.center;


      for (const currentClusterCoords of [currentFirstClusterCoords, currentLastClusterCoords]) {
        // Calculate lateral (2D) distance from the frac plane to the stage origin point
        const pt = turf.point([nextStageCenterCoordinates?.lon, nextStageCenterCoordinates?.lat]);
        const line = turf.lineString([[currentClusterCoords?.top?.start?.lon, currentClusterCoords?.top?.start?.lat], [currentClusterCoords?.top?.end?.lon, currentClusterCoords?.top?.end?.lat]]);
        const lateralDistance = turf.pointToLineDistance(pt, line, { units: 'meters' });

        // Calculate the vertical distance from the frac plane to the stage origin point
        const nextStageCenterDepth = nextStageCenterCoordinates?.tvd;
        const currentFirstClusterTopDepth = currentClusterCoords?.top?.center?.tvd;
        const currentFirstClusterBottomDepth = currentClusterCoords?.bottom?.center?.tvd;
        let verticalDistance = 0;
        if (nextStageCenterDepth > currentFirstClusterTopDepth) { // if frac plane is below the stage origin point
          verticalDistance = nextStageCenterDepth - currentFirstClusterTopDepth;
        } else if (nextStageCenterDepth < currentFirstClusterBottomDepth) { // if frac plane is above the stage origin point
          verticalDistance = currentFirstClusterBottomDepth - nextStageCenterDepth;
        };

        const distance = Math.sqrt(lateralDistance ** 2 + verticalDistance ** 2);

        if (distance <= shadowRadius && (!stagesWithinRadius[stageName] || distance < stagesWithinRadius[stageName])) {
          stagesWithinRadius[stageName] = distance;
        };
      };

    };

    // determine stage length
    const wellName = currentStageName?.split('_')?.[1]?.toLowerCase();
    const trimmedStageNumber = String(Number(String(currentStageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
    const stageClusters = perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
    const clusterNames = stageClusters && Object.keys(stageClusters);
    const numClusters = clusterNames?.length || 0;

    const lastStageNumber = Object.keys(perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0];

    // Calculate stage length
    let stageLength = 0;

    if (trimmedStageNumber === '1') { // for first stage
      stageLength = stageClusters?.['1']?.shotDepth - perfSchedule?.wells?.[wellName]?.stages?.['2']?.clusters?.['1']?.shotDepth;
    } else if (trimmedStageNumber === lastStageNumber) { // for last stage
      const nextToLastStageClusters = perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) - 1]?.clusters;
      stageLength = nextToLastStageClusters?.[Object.keys(nextToLastStageClusters)?.slice(-1)?.[0]]?.shotDepth - stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth;
    } else { // for all other stages
      const previousStageClusters = perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) - 1]?.clusters;
      const nextStageClusters = perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) + 1]?.clusters;
      const topMidPoint = (previousStageClusters?.[Object.keys(previousStageClusters)?.slice(-1)?.[0]]?.shotDepth + stageClusters?.['1']?.shotDepth) / 2;
      const bottomMidPoint = (stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth + nextStageClusters?.['1']?.shotDepth) / 2;
      stageLength = topMidPoint - bottomMidPoint;
    };

    // Calculate cluster spacing
    let clusterSpacing = 0;

    if (numClusters === 1) { // for single-cluster stage
      clusterSpacing = stageLength;
    } else { // for all other stages
      clusterSpacing = (stageClusters?.['1']?.shotDepth - stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth) / (numClusters - 1);
    };

    if (!clusterSpacing > 0) {
      clusterSpacing = formation === 'Montney' ? 20 : formation === 'Duvernay' ? 12 : 35;
    };

    const minDistance = clusterSpacing;

    Object.keys(stagesWithinRadius)?.forEach(stageName => {
      const intrinsicStress = Math.max((fracWidth * youngModulus) / (12 * ((Math.max(stagesWithinRadius[stageName], minDistance) * 3.28084) ** t)) * 0.00689476, 0); // current stage intrinsic stress shadow
      const accumulatedStress = (newSS?.[currentStageName] || 0) / ((Math.max(stagesWithinRadius[stageName], minDistance) * 3.28084) ** t); // current stage previous accumulated stress decayed by the distance
      const totalStress = (newSS?.[stageName] || 0) + intrinsicStress + accumulatedStress; // add previous stress shadow to the value

      newSS[stageName] = totalStress;
    });

    return newSS;

  } catch (error) {
    console.log(error);
  };
};


// function WellboreCrossSection({ currentStageName, wellCoordinates, stageCoordinates3D, clusterCoordinates3D, stageData, stressShadow, settings, surveyData, fracLengthDisplay, formation, client, fracHalfLength }) {

//   const canvasRef = useRef(null);


//   useEffect(() => {
//     try {
//       if (!canvasRef.current || !currentStageName) return; // Ensure canvasRef is not null

//       // find the intersection points from the current stage to all other wellbores
//       const stageLatLon = [stageCoordinates3D?.[currentStageName]?.center?.center?.lat, stageCoordinates3D?.[currentStageName]?.center?.center?.lon];
//       let stageLineCoordinates = calculateLineCoordinates(stageLatLon, 5000);
//       stageLineCoordinates = rotatePolyline(stageLineCoordinates, (settings?.fracAngle || 45));

//       const intersectedWellsCoordinates = [];

//       for (const wellName in wellCoordinates) {
//         const intersectionCoordinates = findIntersectionCoordinates(stageLineCoordinates, wellName, wellCoordinates, surveyData);

//         if (!intersectionCoordinates) continue;

//         const intersectedWellCoordinates = findClosestPointToIntersection(wellName, intersectionCoordinates, wellCoordinates);
//         const closestStageName = wellName?.toLowerCase() === currentStageName?.split('_')?.[1]?.toLowerCase() ? currentStageName : findClosestStageToIntersection(wellName, intersectionCoordinates, clusterCoordinates3D, currentStageName, stageData, settings?.shadowRadius);
//         if (closestStageName) {
//           intersectedWellCoordinates.stageName = closestStageName;
//         };

//         intersectedWellsCoordinates.push(intersectedWellCoordinates);
//       };

//       const canvas = canvasRef.current;
//       const ctx = canvas.getContext('2d');
//       const canvasWidth = canvas.width;
//       const canvasHeight = canvas.height;

//       ctx.clearRect(0, 0, canvasWidth, canvasHeight);

//       // Draw background
//       ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
//       ctx.fillRect(0, 0, canvasWidth, canvasHeight);

//       const centerPoint = calculateCenterPoint(intersectedWellsCoordinates);

//       if (!centerPoint) return;

//       // Calculate the center of the canvas
//       const centerX = canvasWidth / 2;
//       const centerY = canvasHeight / 2;

//       // Length of the 'X' lines (adjust as needed)
//       const lineLength = 5;

//       // Set the line color and width
//       ctx.strokeStyle = 'red'; // You can choose your desired line color
//       ctx.lineWidth = 2; // You can adjust the line width as needed

//       // Draw the 'X' lines at the center point
//       ctx.beginPath();
//       ctx.moveTo(centerX - lineLength, centerY - lineLength);
//       ctx.lineTo(centerX + lineLength, centerY + lineLength);
//       ctx.moveTo(centerX + lineLength, centerY - lineLength);
//       ctx.lineTo(centerX - lineLength, centerY + lineLength);
//       ctx.stroke();


//       // Calculate the maximum frac length
//       const maxFracLength = stageData ? Math.max(...Object.keys(stageData)?.map(stageName => ((stageData?.[stageName]?.fracModel?.halfLength * 2) || (stageData?.[stageName]?.fracModel?.fracArea / settings?.fracHeight) || 600))) : 600;

//       const metersToPixelsX = 1 / (maxFracLength / canvasWidth); // You may need to adjust this based on your canvas scale
//       const metersToPixelsY = 2; // You may need to adjust this based on your canvas scale

//       // Calculate local coordinates for the wellbores
//       const wellboreLocalCoordinates = [];

//       for (const wellbore of intersectedWellsCoordinates) {
//         const { lat, lon, tvd, name, stageName } = wellbore;

//         // calculate the distance between the wellbore and the center point
//         const distanceX = (calculateSignedDistance([centerPoint.lat, centerPoint.lon], [lat, lon]));
//         const distanceY = (centerPoint.tvd - tvd);

//         // Calculate scaled positions for lon and tvd
//         const x = centerX + distanceX * metersToPixelsX;
//         const y = centerY - distanceY * metersToPixelsY;

//         wellboreLocalCoordinates.push({x, y, name, stageName});
//       };

//       // Determine color bounds for stress shadow
//       const values = [];
//       for (let stageName in stressShadow) {
//         const value = stressShadow[stageName];
//         if (value > 0) {
//           values.push(value);
//         };
//       };

//       const minValue = (Math.min(...values) * 0.95) || 0;
//       const maxValue = (Math.max(...values) * 1.05) || 1;

//       const colorScale = scaleSequential(interpolateTurbo).domain([minValue, maxValue]);

//       // Draw the frac rectangles for all intersected wellbores
//       for (const wellbore of wellboreLocalCoordinates?.sort((a, b) => {
//         if (a.stageName === currentStageName) {
//             return 1; // Move 'a' to the end
//         } else if (b.stageName === currentStageName) {
//             return -1; // Move 'b' to the end
//         }
//         return 0; // No change in order
//       })) {
//         const { x, y, stageName } = wellbore;

//         if (!stageName) continue;

//         // Set the rectangle's properties (you can customize these)
//         ctx.fillStyle = rgbToRgba(colorScale(stressShadow[stageName]), stageName === currentStageName ? 1 : 0.75);
//         ctx.strokeStyle = stageName === currentStageName ? 'rgba(0, 0 , 0, 1)' : 'rgba(0, 0, 0, 0.75)'; // Border color
//         ctx.lineWidth = 1; // Border width

//         // Specify the width and height of the rectangle
//         const fracLength = (fracHalfLength > 0 ? fracHalfLength : 300) * 2 * fracLengthDisplay * metersToPixelsX; // Width of the rectangle in meters
//         let fracHeight = (settings?.fracHeight || 70); // Height of the rectangle in meters

//         // Special case for WCAP Duvernay pads
//         if (client === 'wcap' && formation === 'Duvernay') {
//           fracHeight = 30;
//         };

//         fracHeight = fracHeight * metersToPixelsY; // Height of the rectangle in meters

//         const fracX = x - fracLength / 2;
//         const fracY = y - fracHeight / 2;;

//         // Draw the rectangle
//         ctx.fillRect(fracX, fracY, fracLength, fracHeight); // Fill the rectangle
//         ctx.strokeRect(fracX, fracY, fracLength, fracHeight); // Draw the rectangle's border

//       };


//       // Draw the wellbore circles
//       for (const wellbore of wellboreLocalCoordinates) {
//         const { x, y, name, stageName } = wellbore;

//         // Draw a circle at the calculated (x, z) position
//         ctx.beginPath();
//         ctx.arc(x, y, name === currentStageName?.split('_')?.[1] ? 10 : 5, 0, Math.PI * 2);
//         ctx.fillStyle = chartColors[0];
//         ctx.fill();

//         // Draw additional information like wellbore name or ID next to the circle
//         ctx.font = `${name === currentStageName?.split('_')?.[1] ? '12px' : '10px'} Arial`;
//         ctx.fillStyle = '#000';
//         ctx.fillText((stageName?.split('_')?.slice(1, )?.join('_') || name), x + 15, y);
//       };


//       // Draw the scale at the bottom
//       const scaleStartX = 0; // Start of the scale
//       const scaleEndX = canvasWidth;   // End of the scale
//       const tickSpacingX = canvasWidth / 10; // Tick mark spacing

//       ctx.strokeStyle = 'black'; // You can choose your desired scale color
//       ctx.lineWidth = 1; // You can adjust the line width as needed
//       ctx.fillStyle = 'black'; // You can choose your desired text color
//       ctx.font = '12px Arial'; // You can adjust the font size and family as needed

//       for (let x = scaleStartX; x <= scaleEndX + canvasWidth / 10; x += tickSpacingX) {
//         ctx.beginPath();
//         ctx.moveTo(x, canvasHeight - 10); // Adjust the tick mark height as needed
//         ctx.lineTo(x, canvasHeight);
//         ctx.stroke();

//         // Calculate the corresponding meter value and display it
//         const meterValue = (x - canvasWidth / 2) / metersToPixelsX;
//         if (x >= scaleStartX + canvasWidth / 10 && x <= scaleEndX - canvasWidth / 10) {
//           ctx.fillText(meterValue?.toFixed(0), meterValue === 0 ? x - 5 : x - 10, canvasHeight - 15); // Adjust text position as needed
//         };
//       };
//       ctx.fillText('m', canvasWidth - 15, canvasHeight - 15); // Adjust text position as needed

//       // Draw the scale on the left
//       const scaleStartY = 0; // Start of the scale
//       const scaleEndY = canvasHeight; // End of the scale
//       const tickSpacingY = canvasHeight / 10; // Tick mark spacing

//       ctx.strokeStyle = 'black'; // You can choose your desired scale color
//       ctx.lineWidth = 1; // You can adjust the line width as needed
//       ctx.fillStyle = 'black'; // You can choose your desired text color
//       ctx.font = '12px Arial'; // You can adjust the font size and family as needed

//       for (let y = scaleStartY; y <= scaleEndY + canvasHeight / 10; y += tickSpacingY) {
//         ctx.beginPath();
//         ctx.moveTo(10, y); // Adjust the tick mark width as needed
//         ctx.lineTo(0, y);
//         ctx.stroke();

//         // Calculate the corresponding meter value and display it
//         const meterValue = (y - canvasHeight / 2) / metersToPixelsY; // Adjust metersToPixelsY as needed
//         if (y >= scaleStartY + canvasHeight / 10 && y <= scaleEndY - canvasHeight / 10) {
//           ctx.fillText(meterValue, 15, y + 5); // Adjust text position as needed
//         };
//       };
//       ctx.fillText('m', 15, 15); // Adjust text position as needed


//       // Draw a black border around the canvas
//       ctx.strokeStyle = 'black';
//       ctx.lineWidth = 2;
//       ctx.strokeRect(0, 0, canvasWidth, canvasHeight);
//     } catch (error) {
//       console.log(error);
//       return;
//     };

//   }, [currentStageName, wellCoordinates, stageCoordinates3D, clusterCoordinates3D, surveyData, settings?.fracHeight, settings?.fracAngle, settings?.shadowRadius, stageData, stressShadow, fracLengthDisplay, client, formation, fracHalfLength]);

//   return (
//     <canvas
//       ref={canvasRef}
//       width="400"
//       height="400"
//     />
//   );
// };
